/**
 * Owner Console header bell — new-signup notifications.
 *
 * Polls /api/admin/v2/notifications/signups/unread-count/ on a 60s
 * interval (and on tab refocus). Clicking opens a panel that fetches
 * the full signup list, marks them all seen on mount, and links each
 * row to /admin/users for the drill-down.
 *
 * Why a small dedicated component: AdminLayout's header is otherwise
 * stateless. Keeping the bell self-contained avoids leaking
 * intervals + dropdown state into the layout shell.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, X, UserPlus, ExternalLink, RefreshCw } from "lucide-react";
import {
  getSignupUnreadCount,
  listSignupNotifications,
  markSignupsSeen,
} from "../api/owner.api";

const POLL_MS = 60_000;

export default function AdminNotificationBell() {
  const [count, setCount] = useState(0);
  const [overflow, setOverflow] = useState(false);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [lastSeen, setLastSeen] = useState(null);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  // Lightweight popover dismissal — mirrors AdminCreateMenu. We deliberately
  // avoid useDialogDismiss here: its history.pushState/back cleanup undoes the
  // route change when the user clicks a signup row, and its body scroll-lock +
  // focus-trap are modal-grade behaviours wrong for a small header dropdown.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await getSignupUnreadCount();
      setCount(data.count || 0);
      setOverflow(!!data.overflow);
      setLastSeen(data.last_seen_at || null);
    } catch {
      // Silent — the bell is a side feature; do not surface errors in
      // the header. The next poll will retry.
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_MS);
    // Refocusing the tab → immediate refresh, then resume cadence.
    const onFocus = () => refreshCount();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshCount]);

  const openPanel = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await listSignupNotifications();
      setRows(Array.isArray(data.results) ? data.results : []);
      setLastSeen(data.last_seen_at || null);
      // Opening the panel implicitly marks everything as seen — match
      // the standard gmail / linear bell UX. Badge clears immediately
      // so the operator isn't stared at by the same red dot.
      await markSignupsSeen();
      setCount(0);
      setOverflow(false);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const reopen = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listSignupNotifications();
      setRows(Array.isArray(data.results) ? data.results : []);
      setLastSeen(data.last_seen_at || null);
    } finally {
      setLoading(false);
    }
  }, []);

  const lastSeenDate = lastSeen ? new Date(lastSeen) : null;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        onClick={() => (open ? setOpen(false) : openPanel())}
        title={count > 0 ? `${count}${overflow ? "+" : ""} new signups` : "No new signups"}
        className="relative p-1.5 rounded hover:bg-bg-secondary text-text-secondary hover:text-text-primary"
        aria-label="Open signup notifications"
      >
        <Bell className="w-4 h-4" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-4 text-center">
            {overflow ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="New signups"
          className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-hidden rounded-lg border border-border-default bg-bg-primary shadow-xl z-50 flex flex-col"
        >
          <header className="px-3 py-2 border-b border-border-default flex items-center justify-between">
            <div className="text-sm font-semibold flex items-center gap-1.5">
              <UserPlus className="w-4 h-4 text-emerald-300" />
              New signups
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={reopen}
                disabled={loading}
                className="p-1 rounded hover:bg-bg-secondary text-text-secondary"
                title="Refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-bg-secondary text-text-secondary"
                title="Close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </header>
          <div className="overflow-y-auto flex-1">
            {loading && rows.length === 0 ? (
              <div className="p-6 text-center text-xs text-text-secondary">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-center text-xs text-text-secondary">
                No signups in the last 30 days.
              </div>
            ) : (
              rows.map((r) => (
                <SignupRow
                  key={r.id}
                  row={r}
                  lastSeen={lastSeenDate}
                  onPick={() => setOpen(false)}
                />
              ))
            )}
          </div>
          <footer className="px-3 py-2 border-t border-border-default text-[11px] text-text-secondary flex items-center justify-between">
            <span>Last 30 days · max 100 rows</span>
            <Link
              to="/admin/users?segment=all"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1 text-button-primary hover:underline"
            >
              All users <ExternalLink className="w-3 h-3" />
            </Link>
          </footer>
        </div>
      )}
    </div>
  );
}

function SignupRow({ row, lastSeen, onPick }) {
  const joinedDate = row.date_joined ? new Date(row.date_joined) : null;
  // "Unseen" highlight = joined strictly after the last-seen cursor at
  // the moment the panel opened. The badge already cleared on open, but
  // this visual cue tells the operator which rows are the new ones.
  const isUnseen = joinedDate && lastSeen && joinedDate > lastSeen;
  const segmentColor = {
    b2b: "text-blue-300",
    b2c: "text-emerald-300",
    player: "text-amber-300",
    staff: "text-rose-300",
  }[row.segment] || "text-text-secondary";

  return (
    <Link
      to={`/admin/users?segment=all&search=${encodeURIComponent(row.email || "")}`}
      onClick={onPick}
      className={`block px-3 py-2 border-b border-border-default/50 last:border-b-0 hover:bg-bg-secondary text-xs ${
        isUnseen ? "bg-emerald-500/5" : ""
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-mono truncate flex items-center gap-1">
            {isUnseen && (
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
            )}
            {row.email}
          </div>
          <div className="text-[10px] text-text-secondary truncate">
            {row.full_name || "—"}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className={`text-[10px] uppercase tracking-wider ${segmentColor}`}>
            {row.segment}
          </div>
          {joinedDate && (
            <div className="text-[10px] text-text-secondary tabular-nums">
              {joinedDate.toLocaleString("en-IN", {
                day: "2-digit", month: "short",
                hour: "2-digit", minute: "2-digit",
              })}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
